import { api } from './api';
import { Plant, ApiResponse } from '../types/plant';
import { SeedBatch } from '../types/seed';

export interface LabelQueueItem {
  id: number;
  userId: number;
  itemType: 'plant' | 'seed';
  plantId: number | null;
  seedBatchId: number | null;
  addedAt: string;
  createdAt: string;
  plant: Plant | null;
  seedBatch: SeedBatch | null;
}

export interface LabelQueueCount {
  count: number;
}

export interface LabelQueueItemRequest {
  itemType: 'plant' | 'seed';
  plantId?: number;
  seedBatchId?: number;
}

export const labelQueueApi = {
  // Add an item (plant or seed) to the label queue
  addToQueue: async (item: LabelQueueItemRequest): Promise<LabelQueueItem> => {
    const response = await api.post<ApiResponse<LabelQueueItem>>('/label-queue', item);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to add item to queue');
    }
    return response.data.data;
  },

  // Add a plant to the label queue (legacy compatibility)
  addPlantToQueue: async (plantId: string): Promise<LabelQueueItem> => {
    return labelQueueApi.addToQueue({
      itemType: 'plant',
      plantId: parseInt(plantId),
    });
  },

  // Add a seed batch to the label queue
  addSeedToQueue: async (seedBatchId: string): Promise<LabelQueueItem> => {
    return labelQueueApi.addToQueue({
      itemType: 'seed',
      seedBatchId: parseInt(seedBatchId),
    });
  },

  // Add multiple items to the label queue
  addMultipleToQueue: async (items: LabelQueueItemRequest[]): Promise<LabelQueueItem[]> => {
    const response = await api.post<ApiResponse<LabelQueueItem[]>>('/label-queue/batch', {
      items,
    });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to add items to queue');
    }
    return response.data.data;
  },

  // Add multiple plants to the label queue (legacy compatibility)
  addMultiplePlantsToQueue: async (plantIds: string[]): Promise<LabelQueueItem[]> => {
    const items: LabelQueueItemRequest[] = plantIds.map(id => ({
      itemType: 'plant',
      plantId: parseInt(id),
    }));
    return labelQueueApi.addMultipleToQueue(items);
  },

  // Add multiple seeds to the label queue
  addMultipleSeedsToQueue: async (seedBatchIds: string[]): Promise<LabelQueueItem[]> => {
    const items: LabelQueueItemRequest[] = seedBatchIds.map(id => ({
      itemType: 'seed',
      seedBatchId: parseInt(id),
    }));
    return labelQueueApi.addMultipleToQueue(items);
  },

  // Get the user's label queue
  getQueue: async (): Promise<LabelQueueItem[]> => {
    const response = await api.get<ApiResponse<LabelQueueItem[]>>('/label-queue');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to get label queue');
    }
    return response.data.data;
  },

  // Get the user's label queue count
  getQueueCount: async (): Promise<LabelQueueCount> => {
    const response = await api.get<ApiResponse<LabelQueueCount>>('/label-queue/count');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to get queue count');
    }
    return response.data.data;
  },

  // Remove an item from the label queue
  removeFromQueue: async (
    itemType: 'plant' | 'seed',
    itemId: string
  ): Promise<{ deleted: number }> => {
    const response = await api.delete<ApiResponse<{ deleted: number }>>(
      `/label-queue/${itemType}/${itemId}`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to remove item from queue');
    }
    return response.data.data;
  },

  // Remove a plant from the label queue (legacy compatibility)
  removePlantFromQueue: async (plantId: string): Promise<{ deleted: number }> => {
    return labelQueueApi.removeFromQueue('plant', plantId);
  },

  // Remove a seed batch from the label queue
  removeSeedFromQueue: async (seedBatchId: string): Promise<{ deleted: number }> => {
    return labelQueueApi.removeFromQueue('seed', seedBatchId);
  },

  // Clear all items from the label queue
  clearQueue: async (): Promise<{ deleted: number }> => {
    const response = await api.delete<ApiResponse<{ deleted: number }>>('/label-queue');
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to clear queue');
    }
    return response.data.data;
  },
};
